import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { FilterAltOff as ClearFilterIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  People as CustomerIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  FileDownload as ExcelIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import CustomerDialog from '../components/customers/CustomerDialog';
import { Customer } from '../types/customers.types';
import { customersService } from '../services/customers.service';
import { useConfirm } from '../hooks/useConfirm';
import { toast } from 'react-toastify';

const translations = {
  en: {
    title: 'Customers',
    addCustomer: 'Add Customer',
    code: 'Code',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    account: 'Account',
    creditLimit: 'Credit Limit',
    status: 'Status',
    actions: 'Actions',
    search: 'Search...',
    loading: 'Loading...',
    noData: 'No customers found',
    confirmDelete: 'Are you sure you want to delete this customer?',
    active: 'Active',
    inactive: 'Inactive',
    totalCustomers: 'Total Customers',
    activeCustomers: 'Active',
    refresh: 'Refresh',
    edit: 'Edit',
    delete: 'Delete',
    rowsPerPage: 'Rows per page:'
  },
  ar: {
    title: 'العملاء',
    addCustomer: 'إضافة عميل',
    code: 'الكود',
    name: 'الاسم',
    phone: 'الهاتف',
    email: 'البريد',
    account: 'الحساب',
    creditLimit: 'حد الائتمان',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'البحث...',
    loading: 'جاري التحميل...',
    noData: 'لا توجد عملاء',
    confirmDelete: 'هل أنت متأكد من الحذف؟',
    active: 'نشط',
    inactive: 'غير نشط',
    totalCustomers: 'إجمالي العملاء',
    activeCustomers: 'نشط',
    refresh: 'تحديث',
    edit: 'تعديل',
    delete: 'حذف',
    rowsPerPage: 'عدد الصفوف:'
  },
};

const CustomersPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0
  });

  useEffect(() => {
    loadCustomers();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [searchTerm, filterStatus, customers]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const data = await customersService.getAll();
      setCustomers(data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = customers;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (customer) =>
          customer.customerCode.toLowerCase().includes(term) ||
          customer.customerName.toLowerCase().includes(term) ||
          customer.customerNameAr.toLowerCase().includes(term) ||
          customer.phone?.toLowerCase().includes(term) ||
          customer.email?.toLowerCase().includes(term)
      );
    }

    if (filterStatus === 'active') result = result.filter(c => c.isActive);
    if (filterStatus === 'inactive') result = result.filter(c => !c.isActive);

    setFilteredCustomers(result);
    setPage(0);
  };

  const calculateStats = () => {
    const total = customers.length;
    const active = customers.filter(c => c.isActive).length;
    setStats({ total, active });
  };

  const handleAdd = () => {
    setSelectedCustomer(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      await customersService.delete(id);
      loadCustomers();
    } catch (error) {
      toast.error(isRTL ? 'فشل حذف العميل' : 'Failed to delete customer');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedCustomer(undefined);
  };

  const handleSave = () => {
    loadCustomers();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #667eea;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#667eea;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredCustomers.map(customer => `
      <tr>
        <td>${customer.customerCode}</td>
        <td>${isRTL ? customer.customerNameAr : customer.customerName}</td>
        <td>${customer.phone || '-'}</td>
        <td>${customer.email || '-'}</td>
        <td>${customer.accountCode || '-'}</td>
        <td>${customer.creditLimit ? customer.creditLimit.toLocaleString() : '-'}</td>
        <td>${customer.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        h1{text-align:center;color:#667eea;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#667eea;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4 landscape;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${isRTL ? 'قائمة العملاء' : 'Customers List'}</h1>
      <table>
        <thead><tr>
          <th>${t.code}</th>
          <th>${t.name}</th>
          <th>${t.phone}</th>
          <th>${t.email}</th>
          <th>${t.account}</th>
          <th>${t.creditLimit}</th>
          <th>${t.status}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isRTL ? 'العملاء' : 'Customers');

      let currentRow = 1;

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }

      // Company Header
      worksheet.mergeCells('B1:G1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FF667EEA' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:G2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:G3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      // Report Title
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = isRTL ? 'قائمة العملاء' : 'Customers List';
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667EEA' } };
      worksheet.getRow(currentRow).height = 30;
      currentRow++;

      // Date & count
      worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
      const infoCell = worksheet.getCell(`A${currentRow}`);
      infoCell.value = `${isRTL ? 'التاريخ' : 'Date'}: ${new Date().toLocaleDateString('en-GB')} | ${t.totalCustomers}: ${filteredCustomers.length}`;
      infoCell.font = { size: 10, color: { argb: 'FF667EEA' } };
      infoCell.alignment = { horizontal: 'center' };
      currentRow += 2;

      // Headers
      const headers = [t.code, t.name, t.phone, t.email, t.account, t.creditLimit, t.status];
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = headers;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667EEA' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;
      currentRow++;

      // Data rows
      filteredCustomers.forEach((customer, idx) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          customer.customerCode,
          isRTL ? customer.customerNameAr : customer.customerName,
          customer.phone || '-',
          customer.email || '-',
          customer.accountCode || '-',
          customer.creditLimit || 0,
          customer.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive'),
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        row.getCell(6).numFmt = '#,##0.000';
        if (idx % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F3FF' } };
        }
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 14;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 18;
      worksheet.getColumn(4).width = 28;
      worksheet.getColumn(5).width = 16;
      worksheet.getColumn(6).width = 16;
      worksheet.getColumn(7).width = 12;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Customers_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Excel exported successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل تصدير Excel' : 'Excel export failed');
    }
  };

  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{t.totalCustomers}</Typography>
                </Box>
                <GroupIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.active}</Typography>
                  <Typography variant="body2">{t.activeCustomers}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Header & Actions */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CustomerIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadCustomers}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isRTL ? 'طباعة القائمة' : 'Print List'}>
              <IconButton
                color="primary"
                onClick={handlePrintList}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isRTL ? 'تصدير إكسل' : 'Export Excel'}>
              <IconButton
                color="primary"
                onClick={exportToExcel}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <ExcelIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
              }}
            >
              {t.addCustomer}
            </Button>
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 2 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{isRTL ? 'الحالة' : 'Status'}</InputLabel>
            <Select value={filterStatus} label={isRTL ? 'الحالة' : 'Status'} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
              <MenuItem value="active">{t.active}</MenuItem>
              <MenuItem value="inactive">{t.inactive}</MenuItem>
            </Select>
          </FormControl>
          {(searchTerm || filterStatus) && (
            <Button size="small" startIcon={<ClearFilterIcon />} onClick={() => { setSearchTerm(''); setFilterStatus(''); }}>
              {isRTL ? 'مسح' : 'Clear'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.code}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.name}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.phone}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.email}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.account}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.creditLimit}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCustomers.map((customer) => (
                  <TableRow key={customer.customerId} hover sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}>
                    <TableCell>
                      <Typography fontWeight="bold" color="#667eea">{customer.customerCode}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {isRTL ? customer.customerNameAr : customer.customerName}
                      </Typography>
                    </TableCell>
                    <TableCell>{customer.phone || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {customer.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {customer.accountCode ? (
                        <>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {customer.accountCode}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {isRTL ? customer.accountNameAr : customer.accountName}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.creditLimit
                        ? customer.creditLimit.toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={customer.isActive ? t.active : t.inactive}
                        color={customer.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t.edit}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(customer)}
                            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.delete}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(customer.customerId)}
                            sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={filteredCustomers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`
          }
        />
      </TableContainer>

      <CustomerDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        customer={selectedCustomer}
        onSave={handleSave}
      />
    </Box>
  );
};

export default CustomersPage;